import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Briefcase, ChevronDown, Plus } from "lucide-react";
import { cn } from "@/lib/utils";
import { NavLink } from "@/shared/layout/navLink";
import { useReadProjectsApiProjectsGet } from "@/type-gen/api";

interface ProjectsNavItemProps {
  onNavigate?: () => void;
  onCreateProject: () => void;
}

export const ProjectsNavItem: React.FC<ProjectsNavItemProps> = ({
  onNavigate,
  onCreateProject,
}) => {
  const [hovering, setHovering] = useState(false);
  const [pinned, setPinned] = useState(false);
  const open = hovering || pinned;
  const { data } = useReadProjectsApiProjectsGet({
    page: 1,
    page_size: 4,
    search: null,
    sort_by: "updated_at",
    sort_order: "desc",
  });
  const latestProjects = data?.data?.items ?? [];

  return (
    <div
      className="relative"
      onMouseEnter={() => setHovering(true)}
      onMouseLeave={() => setHovering(false)}
    >
      <div className="flex items-center gap-0.5">
        <NavLink
          to="/projects"
          variant="sidebar"
          className="flex-1"
          {...(onNavigate ? { onClick: onNavigate } : {})}
        >
          <Briefcase className="h-4 w-4 shrink-0" />
          Projects
        </NavLink>
        <button
          type="button"
          aria-expanded={open}
          aria-label={open ? "Hide latest projects" : "Show latest projects"}
          onClick={() => setPinned((current) => !current)}
          className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-muted-foreground hover:bg-foreground/5 hover:text-foreground"
        >
          <ChevronDown
            className={cn("h-4 w-4 transition-transform", open && "rotate-180")}
          />
        </button>
      </div>

      {open ? (
        <div className="mt-0.5 ml-4 flex flex-col gap-0.5 border-l border-border pl-2">
          {latestProjects.length === 0 ? (
            <p className="px-2 py-1.5 text-xs text-muted-foreground">
              No projects yet
            </p>
          ) : (
            latestProjects.map((project) => (
              <Link
                key={project.id}
                to={`/projects/${project.id}/shotlist`}
                onClick={onNavigate}
                className="min-w-0 rounded-md px-2 py-1.5 hover:bg-foreground/5"
              >
                <span className="block truncate text-sm text-foreground">
                  {project.name}
                </span>
                {project.client.name ? (
                  <span className="block truncate text-[11px] text-muted-foreground">
                    {project.client.name}
                  </span>
                ) : null}
              </Link>
            ))
          )}
          <button
            type="button"
            onClick={() => {
              setPinned(false);
              setHovering(false);
              onCreateProject();
            }}
            className="flex items-center gap-1.5 rounded-md px-2 py-1.5 text-sm font-medium text-foreground hover:bg-foreground/5"
          >
            <Plus className="h-3.5 w-3.5" />
            New project
          </button>
        </div>
      ) : null}
    </div>
  );
};
